import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { StatStrip } from "../../../../components/UIToolkit/molecules/StatStrip";
import type { BuildingFigures } from "../../network/networkMath";
import type { SummaryFormatters } from "./useSummaryFormatters";

type Props = {
	totals: BuildingFigures;
	/** Share of hours with nothing to review; null when there are no hours at all. */
	clean: number | null;
	loading: boolean;
	formatters: SummaryFormatters;
};

/** The Summary's four headline figures across the selected buildings. */
export const SummaryTiles: FC<Props> = ({ totals, clean, loading, formatters: { formatHours, formatCount, formatPercent } }) => {
	const { t } = useTranslation();
	return (
		<StatStrip
			variant="band"
			// Each tile's caption is a short sentence; wrapping cells keeps them from running into each other.
			wrapCells
			loading={loading}
			dataTestId="pbj-network-summary-tiles"
			items={[
				{
					label: t("pbj_model.network.summary.tiles.total_hours"),
					value: formatHours(totals.totalHours),
					caption: t("pbj_model.network.summary.tiles.employee_days", { count: totals.employeeDays, days: formatCount(totals.employeeDays) }),
					dataTestId: "pbj-network-summary-tile-total",
				},
				{
					label: t("pbj_model.network.summary.tiles.clean"),
					value: clean === null ? "—" : formatPercent(clean),
					caption: t("pbj_model.network.summary.tiles.clean_caption"),
					dataTestId: "pbj-network-summary-tile-clean",
				},
				{
					label: t("pbj_model.network.summary.tiles.missed_punches"),
					value: formatCount(totals.missingPunchCount),
					caption: t("pbj_model.network.summary.tiles.missed_punches_caption"),
					dataTestId: "pbj-network-summary-tile-missed",
				},
				{
					label: t("pbj_model.network.summary.tiles.unmapped_jobs"),
					value: formatCount(totals.unmappedJobCount),
					caption: t("pbj_model.network.summary.tiles.unmapped_jobs_caption"),
					dataTestId: "pbj-network-summary-tile-unmapped",
				},
			]}
		/>
	);
};
